import React, { useState, useEffect, useCallback } from 'react';
import './ProductManagement.css';

const ProductManagement = ({ adminPassword }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    category: 'all',
    price: '',
    image: '',
    description: '',
    colors: ['black', 'white'],
    sizes: ['S', 'M', 'L', 'XL', '2XL'],
    stock: '50',
    colorVariants: {
      black: {
        available: true,
        frontImage: '',
        backImage: ''
      },
      white: {
        available: true,
        frontImage: '',
        backImage: ''
      }
    }
  });

  const fetchProducts = useCallback(async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || '/api';
      const response = await fetch(`${apiUrl}/admin/products`, {
        headers: {
          'x-admin-password': adminPassword
        }
      });
      const data = await response.json();
      setProducts(data.products || data);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  }, [adminPassword]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleColorsChange = (e) => {
    const value = e.target.value;
    const colorsArray = value.split(',').map(c => c.trim()).filter(c => c);
    setFormData(prev => ({ ...prev, colors: colorsArray }));
  };

  const handleSizesChange = (e) => {
    const options = e.target.options;
    const selected = [];
    for (let i = 0; i < options.length; i++) {
      if (options[i].selected) {
        selected.push(options[i].value);
      }
    }
    setFormData(prev => ({ ...prev, sizes: selected }));
  };

  const handleImageUpload = async (e, colorVariant = null, side = null) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingImage(true);
    const formDataToSend = new FormData();
    formDataToSend.append('image', file);

    try {
      const apiUrl = process.env.REACT_APP_API_URL || '/api';
      const response = await fetch(`${apiUrl}/admin/upload-image`, {
        method: 'POST',
        headers: {
          'x-admin-password': adminPassword
        },
        body: formDataToSend
      });

      const data = await response.json();
      if (data.success) {
        if (colorVariant && side) {
          // Update specific color variant image
          setFormData(prev => ({
            ...prev,
            colorVariants: {
              ...prev.colorVariants,
              [colorVariant]: {
                ...prev.colorVariants[colorVariant],
                [side]: data.imageUrl
              }
            }
          }));
        } else {
          // Update main image (for backward compatibility)
          setFormData(prev => ({ ...prev, image: data.imageUrl }));
        }
        alert('Image uploaded successfully!');
      } else {
        alert('Failed to upload image: ' + data.message);
      }
    } catch (error) {
      console.error('Image upload error:', error);
      alert('Error uploading image');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const apiUrl = process.env.REACT_APP_API_URL || '/api';
      const url = editingProduct 
        ? `${apiUrl}/admin/products/${editingProduct.id}`
        : `${apiUrl}/admin/products`;
      
      const method = editingProduct ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'x-admin-password': adminPassword
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();
      
      if (data.success) {
        alert(editingProduct ? 'Product updated!' : 'Product added!');
        resetForm();
        fetchProducts();
      } else {
        alert('Error: ' + data.message);
      }
    } catch (error) {
      console.error('Submit error:', error);
      alert('Error saving product');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      category: product.category,
      price: product.price.toString(),
      image: product.image,
      description: product.description,
      colors: product.colors,
      sizes: product.sizes,
      stock: product.stock.toString(),
      colorVariants: product.colorVariants || {
        black: { available: true, frontImage: '', backImage: '' },
        white: { available: true, frontImage: '', backImage: '' }
      }
    });
    setShowForm(true);
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) {
      return;
    }

    try {
      const apiUrl = process.env.REACT_APP_API_URL || '/api';
      const response = await fetch(`${apiUrl}/admin/products/${productId}`, {
        method: 'DELETE',
        headers: {
          'x-admin-password': adminPassword
        }
      });

      const data = await response.json();
      if (data.success) {
        alert('Product deleted!');
        fetchProducts();
      }
    } catch (error) {
      console.error('Delete error:', error);
      alert('Error deleting product');
    }
  };

  const handleToggleStatus = async (productId, currentStatus) => {
    const newStatus = currentStatus === 'published' ? 'draft' : 'published';
    const action = newStatus === 'published' ? 'publish' : 'unpublish';
    
    if (!window.confirm(`Are you sure you want to ${action} this product?`)) {
      return;
    }

    try {
      const apiUrl = process.env.REACT_APP_API_URL || '/api';
      const response = await fetch(`${apiUrl}/admin/products/${productId}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'x-admin-password': adminPassword
        },
        body: JSON.stringify({ status: newStatus })
      });

      const data = await response.json();
      if (data.success) {
        alert(`Product ${action}ed successfully!`);
        fetchProducts();
      } else {
        alert('Error: ' + data.message);
      }
    } catch (error) {
      console.error('Toggle status error:', error);
      alert('Error updating product status');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      category: 'all',
      price: '',
      image: '',
      description: '',
      colors: ['black', 'white'],
      sizes: ['S', 'M', 'L', 'XL', '2XL'],
      stock: '50',
      colorVariants: {
        black: { available: true, frontImage: '', backImage: '' },
        white: { available: true, frontImage: '', backImage: '' }
      }
    });
    setEditingProduct(null);
    setShowForm(false);
  };

  return (
    <div className="product-management">
      <div className="pm-header">
        <h2>Product Management</h2>
        <button 
          className="pm-btn-primary"
          onClick={() => setShowForm(!showForm)}
        >
          {showForm ? '✕ Cancel' : '+ Add New Product'}
        </button>
      </div>

      {showForm && (
        <div className="product-form-container">
          <h3>{editingProduct ? 'Edit Product' : 'Add New Product'}</h3>
          <form onSubmit={handleSubmit} className="product-form">
            <div className="form-row">
              <div className="form-group">
                <label>Product Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="e.g., Classic Black Tee"
                  required
                />
              </div>

              <div className="form-group">
                <label>Category *</label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  required
                >
                  <option value="men">Men</option>
                  <option value="women">Women</option>
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Price (₹) *</label>
                <input
                  type="number"
                  name="price"
                  value={formData.price}
                  onChange={handleInputChange}
                  placeholder="599"
                  min="0"
                  required
                />
              </div>

              <div className="form-group">
                <label>Stock Quantity *</label>
                <input
                  type="number"
                  name="stock"
                  value={formData.stock}
                  onChange={handleInputChange}
                  placeholder="50"
                  min="0"
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label>Description *</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Brief description of the product"
                rows="3"
                required
              />
            </div>

            {/* Color Variants Section */}
            <div className="color-variants-section">
              <h4>Color Variants (Black & White T-Shirts)</h4>
              <p className="help-text">Upload front and back images for each color variant. If a variant is not available, uncheck the checkbox.</p>
              
              {/* Black Variant */}
              <div className="variant-upload-group">
                <div className="variant-header">
                  <h5>Black Variant</h5>
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.colorVariants.black.available}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        colorVariants: {
                          ...prev.colorVariants,
                          black: { ...prev.colorVariants.black, available: e.target.checked }
                        }
                      }))}
                    />
                    Available
                  </label>
                </div>
                
                {formData.colorVariants.black.available && (
                  <div className="variant-images">
                    <div className="image-upload-box">
                      <label>Front Image *</label>
                      {formData.colorVariants.black.frontImage && (
                        <div className="image-preview-small">
                          <img src={formData.colorVariants.black.frontImage} alt="Black Front" />
                        </div>
                      )}
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageUpload(e, 'black', 'frontImage')}
                        disabled={uploadingImage}
                        id="black-front-upload"
                      />
                      <label htmlFor="black-front-upload" className="upload-button-small">
                        {uploadingImage ? 'Uploading...' : '📁 Upload'}
                      </label>
                      <input
                        type="url"
                        value={formData.colorVariants.black.frontImage}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          colorVariants: {
                            ...prev.colorVariants,
                            black: { ...prev.colorVariants.black, frontImage: e.target.value }
                          }
                        }))}
                        placeholder="Or paste URL"
                      />
                    </div>
                    
                    <div className="image-upload-box">
                      <label>Back Image *</label>
                      {formData.colorVariants.black.backImage && (
                        <div className="image-preview-small">
                          <img src={formData.colorVariants.black.backImage} alt="Black Back" />
                        </div>
                      )}
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageUpload(e, 'black', 'backImage')}
                        disabled={uploadingImage}
                        id="black-back-upload"
                      />
                      <label htmlFor="black-back-upload" className="upload-button-small">
                        {uploadingImage ? 'Uploading...' : '📁 Upload'}
                      </label>
                      <input
                        type="url"
                        value={formData.colorVariants.black.backImage}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          colorVariants: {
                            ...prev.colorVariants,
                            black: { ...prev.colorVariants.black, backImage: e.target.value }
                          }
                        }))}
                        placeholder="Or paste URL"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* White Variant */}
              <div className="variant-upload-group">
                <div className="variant-header">
                  <h5>White Variant</h5>
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.colorVariants.white.available}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        colorVariants: {
                          ...prev.colorVariants,
                          white: { ...prev.colorVariants.white, available: e.target.checked }
                        }
                      }))}
                    />
                    Available
                  </label>
                </div>
                
                {formData.colorVariants.white.available && (
                  <div className="variant-images">
                    <div className="image-upload-box">
                      <label>Front Image *</label>
                      {formData.colorVariants.white.frontImage && (
                        <div className="image-preview-small">
                          <img src={formData.colorVariants.white.frontImage} alt="White Front" />
                        </div>
                      )}
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageUpload(e, 'white', 'frontImage')}
                        disabled={uploadingImage}
                        id="white-front-upload"
                      />
                      <label htmlFor="white-front-upload" className="upload-button-small">
                        {uploadingImage ? 'Uploading...' : '📁 Upload'}
                      </label>
                      <input
                        type="url"
                        value={formData.colorVariants.white.frontImage}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          colorVariants: {
                            ...prev.colorVariants,
                            white: { ...prev.colorVariants.white, frontImage: e.target.value }
                          }
                        }))}
                        placeholder="Or paste URL"
                      />
                    </div>
                    
                    <div className="image-upload-box">
                      <label>Back Image *</label>
                      {formData.colorVariants.white.backImage && (
                        <div className="image-preview-small">
                          <img src={formData.colorVariants.white.backImage} alt="White Back" />
                        </div>
                      )}
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageUpload(e, 'white', 'backImage')}
                        disabled={uploadingImage}
                        id="white-back-upload"
                      />
                      <label htmlFor="white-back-upload" className="upload-button-small">
                        {uploadingImage ? 'Uploading...' : '📁 Upload'}
                      </label>
                      <input
                        type="url"
                        value={formData.colorVariants.white.backImage}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          colorVariants: {
                            ...prev.colorVariants,
                            white: { ...prev.colorVariants.white, backImage: e.target.value }
                          }
                        }))}
                        placeholder="Or paste URL"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="form-group">
              <label>Main Product Image (Optional - for listing)</label>
              <div className="image-upload-section">
                {formData.image && (
                  <div className="image-preview">
                    <img src={formData.image} alt="Preview" />
                  </div>
                )}
                <div className="upload-controls">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageUpload(e)}
                    disabled={uploadingImage}
                    id="image-upload"
                  />
                  <label htmlFor="image-upload" className="upload-button">
                    {uploadingImage ? 'Uploading...' : '📁 Upload Image'}
                  </label>
                  <small>Or paste image URL below</small>
                  <input
                    type="url"
                    name="image"
                    value={formData.image}
                    onChange={handleInputChange}
                    placeholder="https://example.com/image.jpg"
                  />
                </div>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Available Colors *</label>
                <input
                  type="text"
                  value={formData.colors.join(', ')}
                  onChange={handleColorsChange}
                  placeholder="black, white, blue"
                />
                <small>Separate colors with commas</small>
              </div>

              <div className="form-group">
                <label>Available Sizes *</label>
                <select
                  multiple
                  value={formData.sizes}
                  onChange={handleSizesChange}
                  size="5"
                >
                  <option value="XS">XS</option>
                  <option value="S">S</option>
                  <option value="M">M</option>
                  <option value="L">L</option>
                  <option value="XL">XL</option>
                  <option value="2XL">2XL</option>
                </select>
                <small>Hold Ctrl/Cmd to select multiple</small>
              </div>
            </div>

            <div className="form-actions">
              <button type="submit" disabled={loading || uploadingImage} className="btn-submit">
                {loading ? 'Saving...' : (editingProduct ? 'Update Product' : 'Add Product')}
              </button>
              <button type="button" onClick={resetForm} className="btn-cancel">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="products-list">
        <h3>All Products ({products.length})</h3>
        <div className="products-grid">
          {products.map(product => (
            <div key={product.id} className={`product-item ${product.status === 'draft' ? 'draft-product' : 'published-product'}`}>
              <div className="product-image">
                <img src={product.image} alt={product.name} />
                <span className={`category-badge ${product.category}`}>
                  {product.category}
                </span>
                <span className={`product-status-badge ${product.status || 'draft'}`}>
                  {product.status === 'published' ? '✓ Published' : '📝 Draft'}
                </span>
              </div>
              <div className="product-details">
                <h4>{product.name}</h4>
                <p className="description">{product.description}</p>
                <div className="product-meta">
                  <span className="price">₹{product.price}</span>
                  <span className="stock">Stock: {product.stock}</span>
                </div>
                <div className="product-attrs">
                  <div>
                    <strong>Colors:</strong> {product.colors.join(', ')}
                  </div>
                  <div>
                    <strong>Sizes:</strong> {product.sizes.join(', ')}
                  </div>
                </div>
                <div className="product-actions">
                  <button 
                    className={`pm-btn-status ${product.status === 'published' ? 'btn-unpublish' : 'btn-publish'}`}
                    onClick={() => handleToggleStatus(product.id, product.status || 'draft')}
                    title={product.status === 'published' ? 'Unpublish (hide from customers)' : 'Publish (make visible to customers)'}
                  >
                    {product.status === 'published' ? '👁️‍🗨️ Unpublish' : '🚀 Publish'}
                  </button>
                  <button 
                    className="pm-btn-edit"
                    onClick={() => handleEdit(product)}
                  >
                    ✏️ Edit
                  </button>
                  <button 
                    className="pm-btn-delete"
                    onClick={() => handleDelete(product.id)}
                  >
                    🗑️ Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ProductManagement;
